import { createContext, useContext, useRef, useCallback, useEffect } from 'react';

/**
 * TEMA C3 (video de Alian "Cómo guardar material en Well Construction"):
 * evita perder filas del Cutting Log cargadas y no guardadas cuando el
 * usuario navega a otra pantalla, recarga o cierra la pestaña. Cualquier
 * pantalla puede marcar "dirty" (ej. CuttingLogCard); el Sidebar y el botón
 * "Back" de Job Detail consultan `confirmLeave()` antes de navegar.
 * No bloquea nada cuando no hay cambios sin guardar (caso normal, 99% del
 * tiempo) — es aditivo, no cambia el comportamiento existente de la app.
 */
const UnsavedChangesContext = createContext({
  setDirty: () => {},
  confirmLeave: () => true,
});

const DEFAULT_MESSAGE = 'Tenés cambios sin guardar. ¿Salir sin guardar?';

export function UnsavedChangesProvider({ children }) {
  const dirtyRef = useRef(false);
  const messageRef = useRef(DEFAULT_MESSAGE);

  const setDirty = useCallback((isDirty, message) => {
    dirtyRef.current = !!isDirty;
    messageRef.current = message || DEFAULT_MESSAGE;
  }, []);

  const confirmLeave = useCallback(() => {
    if (!dirtyRef.current) return true;
    const ok = window.confirm(messageRef.current);
    return ok;
  }, []);

  // Refresh / close tab
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Browser Back/Forward buttons — best effort: if the user cancels, we
  // push the history entry forward again to "undo" the back navigation.
  useEffect(() => {
    const handlePopState = () => {
      if (!dirtyRef.current) return;
      const ok = window.confirm(messageRef.current);
      if (!ok) window.history.forward();
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return (
    <UnsavedChangesContext.Provider value={{ setDirty, confirmLeave }}>
      {children}
    </UnsavedChangesContext.Provider>
  );
}

export const useUnsavedChanges = () => useContext(UnsavedChangesContext);
